import { useEffect, useState, type FormEvent } from "react";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { submitGroupCapInquiry } from "@/lib/api";
import { createErrorMessage } from "@/lib/error-message";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  groupId: string;
  groupName: string;
  /** Optional default for the desired count input — typically current+1 or
   * a round-up like 100 when the admin explicitly hit the cap. */
  defaultDesired?: number;
}

// GroupCapInquiryDialog mirrors the ExamInquiryCard flow: collect a free-
// form note plus a "how many members do you need" hint and forward to
// Discord via the inquiries endpoint. The dialog is opened both from the
// admin invite UI (when sending past 50 fails) and from the invitation
// accept flow (when AddMember returns 409).
export function GroupCapInquiryDialog({
  open,
  onOpenChange,
  groupId,
  groupName,
  defaultDesired,
}: Props) {
  const [desired, setDesired] = useState<string>("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDesired(defaultDesired ? String(defaultDesired) : "");
    setNote("");
    setError("");
    setDone(false);
  }, [open, defaultDesired]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const desiredN = desired.trim() ? Number(desired.trim()) : 0;
      if (desired.trim() && (Number.isNaN(desiredN) || desiredN <= 50)) {
        setError("원하는 인원은 51명 이상의 숫자로 입력해주세요");
        setSubmitting(false);
        return;
      }
      await submitGroupCapInquiry({
        group_id: groupId,
        group_name: groupName,
        desired_member_count: desiredN,
        note: note.trim() || undefined,
      });
      setDone(true);
    } catch (err) {
      setError(createErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>그룹 인원 한도 증설 문의</DialogTitle>
          <DialogDescription>
            한 그룹은 기본 50명까지 가입할 수 있습니다. 더 많은 인원이 필요하면
            아래 내용으로 디스코드 운영팀에 문의가 전달됩니다.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <CheckCircle2 className="size-10 text-emerald-500" />
            <p className="text-sm text-muted-foreground">
              문의가 전달되었습니다. 운영팀 답변을 기다려주세요.
            </p>
            <Button onClick={() => onOpenChange(false)}>닫기</Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="rounded-lg border bg-muted/40 px-3 py-2 text-sm">
              <span className="text-muted-foreground">대상 그룹: </span>
              <span className="font-medium">{groupName}</span>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="cap-desired">원하는 인원 (선택)</Label>
              <Input
                id="cap-desired"
                type="number"
                min={51}
                value={desired}
                onChange={(e) => setDesired(e.target.value)}
                placeholder="예: 100"
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="cap-note">메모 (선택)</Label>
              <textarea
                id="cap-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={4}
                maxLength={2000}
                placeholder="필요한 사유나 운영 관련 메모를 적어주세요"
                className="rounded-lg border bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>

            {error && (
              <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </p>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                취소
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Send className="size-4" />
                )}
                문의 보내기
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
